import { useMemo } from 'react'
import { upperFirst } from 'lodash'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Button, Dropdown, Pill } from '@/ui'
import { mtc } from '@/styles'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { filterControlStyles } from './styles'

export type AccountFilter = 'disconnected' | 'accountType' | 'bank'

export interface AccountFilterState {
  active: AccountFilter[]
  values: Partial<Record<AccountFilter, string>>
}

export const EMPTY_ACCOUNT_FILTERS: AccountFilterState = {
  active: [],
  values: {},
}

const FILTER_LABELS: Record<AccountFilter, string> = {
  disconnected: 'Disconnected accounts',
  accountType: 'Account type',
  bank: 'Bank',
}

const FILTER_ORDER: AccountFilter[] = ['disconnected', 'accountType', 'bank']

const shouldIncludeAccount = (
  account: FinancialAccount,
  filters: AccountFilterState,
  erroredPlaidItemIds: ReadonlyMap<number, unknown>
): boolean => {
  // Filtering for disconnected accounts hides the ones that sync fine.
  const isConnected =
    account.plaidItemId === null ||
    !erroredPlaidItemIds.has(account.plaidItemId)
  if (filters.active.includes('disconnected') && isConnected) return false

  // An account type is selected, so accounts of any other type are hidden.
  const hasOtherAccountType = account.subtype !== filters.values.accountType
  if (filters.values.accountType && hasOtherAccountType) return false

  // A bank is selected, so accounts from any other bank are hidden.
  const hasOtherBank = account.plaidInstitutionName !== filters.values.bank
  if (filters.values.bank && hasOtherBank) return false

  return true
}

/**
 * Applies the active account filters. Disconnected → only accounts whose plaid item has an
 * error; Account type / Bank → exact match on the selected value (once one is chosen).
 */
export const applyAccountFilters = (
  accounts: FinancialAccount[],
  filters: AccountFilterState,
  erroredPlaidItemIds: ReadonlyMap<number, unknown>
): FinancialAccount[] =>
  accounts.filter((account) =>
    shouldIncludeAccount(account, filters, erroredPlaidItemIds)
  )

/**
 * The filter bar for the bank-accounts list: a green "Disconnected accounts" pill plus dashed
 * "Account type" / "Bank" pills whose value menus are the accounts' distinct subtypes / banks.
 * Controlled — owns no state; the parent holds an {@link AccountFilterState} and applies it via
 * {@link applyAccountFilters}.
 */
export const AccountFilters = ({
  accounts,
  filters,
  onChange,
}: {
  accounts: FinancialAccount[]
  filters: AccountFilterState
  onChange: (next: AccountFilterState) => void
}) => {
  const { availableSubtypes, availableBanks } = useMemo(
    () => ({
      availableSubtypes: Array.from(
        new Set(accounts.map((account) => account.subtype).filter(Boolean))
      ),
      availableBanks: Array.from(
        new Set(
          accounts
            .map((account) => account.plaidInstitutionName)
            .filter((name): name is string => Boolean(name))
        )
      ),
    }),
    [accounts]
  )

  const addFilter = (filter: AccountFilter) => {
    if (filters.active.includes(filter)) return
    onChange({ ...filters, active: [...filters.active, filter] })
  }
  const removeFilter = (filter: AccountFilter) =>
    onChange({
      active: filters.active.filter((f) => f !== filter),
      values: { ...filters.values, [filter]: undefined },
    })
  const selectValue = (filter: AccountFilter, value: string) =>
    onChange({ ...filters, values: { ...filters.values, [filter]: value } })

  const renderFilterPill = (filter: AccountFilter) => {
    if (filter === 'disconnected') {
      return (
        <Pill
          key={filter}
          variant="outline"
          color="green"
          size="sm"
          uppercase={false}
          dismissible
          onDismiss={() => removeFilter(filter)}
          className={mtc(filterControlStyles())}
        >
          {FILTER_LABELS[filter]}
        </Pill>
      )
    }

    const values = filter === 'accountType' ? availableSubtypes : availableBanks
    const selectedValue = filters.values[filter]
    const label = selectedValue
      ? `${FILTER_LABELS[filter]}: ${upperFirst(selectedValue)}`
      : FILTER_LABELS[filter]
    const valueItems = values.map((value) => (
      <Dropdown.Item key={value} onSelect={() => selectValue(filter, value)}>
        {upperFirst(value)}
      </Dropdown.Item>
    ))

    return (
      <Dropdown key={filter}>
        <Dropdown.Trigger asChild>
          <Pill
            variant={selectedValue ? 'outline' : 'dashed'}
            color="green"
            size="sm"
            uppercase={false}
            dismissible
            onDismiss={() => removeFilter(filter)}
            className={mtc(filterControlStyles(), 'cursor-pointer')}
          >
            {label}
          </Pill>
        </Dropdown.Trigger>
        <Dropdown.Content align="start">{valueItems}</Dropdown.Content>
      </Dropdown>
    )
  }

  const filterPills = filters.active.map(renderFilterPill)

  const availableFilters = FILTER_ORDER.filter(
    (filter) => !filters.active.includes(filter)
  )
  const filterMenuItems = availableFilters.map((filter) => (
    <Dropdown.Item key={filter} onSelect={() => addFilter(filter)}>
      {FILTER_LABELS[filter]}
    </Dropdown.Item>
  ))

  return (
    <div className="flex items-center gap-2">
      {filterPills}
      {availableFilters.length > 0 && (
        <Dropdown>
          <Dropdown.Trigger asChild>
            <Button
              variant="dashed"
              iconLeft={faPlus}
              className={mtc(filterControlStyles(), 'rounded-full')}
            >
              Filter
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Content align="start">{filterMenuItems}</Dropdown.Content>
        </Dropdown>
      )}
    </div>
  )
}
